import { useEffect, useState } from 'react';
import { obterClienteApi, atualizarConfigCliente } from '../api';
import { Alert } from './common';

/**
 * Competência final: até quando o cliente é responsabilidade da Átrio.
 *
 * A trava de escrituração protege o passado. Isto protege o depois: encerrado o
 * contrato, julho de um cliente distratado em junho abria como mês comum e o
 * relatório saía com a nossa assinatura, sem nada na tela dizendo que aquele mês
 * não é mais nosso.
 *
 * O aviso não impede nada de propósito. Fechar o mês continua sendo o
 * fechamento; isto é o que o contador precisa LER antes de escriturar.
 */

const MES = ['', 'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho',
  'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

const br = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('/') : null);
/** 'AAAA-MM-DD' -> '06/2026', que é como a competência se lê. */
const comoCompetencia = (d) => {
  if (!d) return null;
  const [ano, mes] = String(d).slice(0, 10).split('-');
  return `${mes}/${ano}`;
};

/**
 * Lê a competência final do cliente. Uma busca por cliente, guardada enquanto a
 * tela viver: o campo muda uma vez na vida do contrato.
 */
export function useCompetenciaFinal(clienteId) {
  const [dados, setDados] = useState(null);

  useEffect(() => {
    let vivo = true;
    if (!clienteId) { setDados(null); return undefined; }
    obterClienteApi(clienteId)
      .then((c) => {
        if (!vivo) return;
        setDados(c?.competencia_final ? {
          fim: String(c.competencia_final).slice(0, 10),
          motivo: c.competencia_final_motivo || null,
          por: c.competencia_final_por || null,
          em: c.competencia_final_em || null,
          razao_social: c.razao_social || '',
        } : null);
      })
      .catch(() => { if (vivo) setDados(null); });
    return () => { vivo = false; };
  }, [clienteId]);

  return dados;
}

/** A competência escolhida já passou do fim do contrato? */
export function foraDaCarteira(fim, ano, mes) {
  if (!fim || !ano || !mes) return false;
  const escolhida = `${ano}-${String(mes).padStart(2, '0')}`;
  return escolhida > String(fim).slice(0, 7);
}

/**
 * Faixa de aviso. Entra em qualquer tela que escolha competência; some sozinha
 * quando o cliente não tem fim de contrato ou quando o mês ainda é nosso.
 */
export function AvisoForaDaCarteira({ clienteId, ano, mes, acao = 'Este relatório' }) {
  const dados = useCompetenciaFinal(clienteId);
  if (!dados || !foraDaCarteira(dados.fim, ano, mes)) return null;

  return (
    <div style={{
      border: '1px solid var(--al-critica-fg)', borderLeftWidth: 4, borderRadius: 8,
      background: 'var(--al-critica-bg)', color: 'var(--al-critica-fg)',
      padding: '12px 16px', margin: '0 0 14px',
    }}>
      <div style={{ fontWeight: 700, fontSize: 14 }}>
        {MES[Number(mes)]}/{ano} não é mais responsabilidade da Átrio
      </div>
      <div className="small" style={{ marginTop: 4, lineHeight: 1.45 }}>
        A última competência sob nossa responsabilidade é <b>{comoCompetencia(dados.fim)}</b>,
        encerrada em {br(dados.fim)}.
        {dados.motivo ? <> {dados.motivo}</> : null}
        {' '}{acao} cobre período posterior ao fim do contrato: confira antes de assinar ou entregar.
      </div>
      {(dados.por || dados.em) && (
        <div className="small" style={{ marginTop: 4, opacity: .8 }}>
          Encerramento registrado
          {dados.por ? <> por {dados.por}</> : null}
          {dados.em ? <> em {new Date(dados.em).toLocaleDateString('pt-BR')}</> : null}.
        </div>
      )}
    </div>
  );
}

/**
 * Cartão de encerramento da carteira, para a tela de fechamento. Grava a
 * competência final, o motivo e o autor; o backend guarda no mesmo log da trava.
 */
export function EncerramentoCarteira({ clienteId, onMudou }) {
  const [cli, setCli] = useState(null);
  const [comp, setComp] = useState('');
  const [motivo, setMotivo] = useState('');
  const [msg, setMsg] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  const carregar = () => {
    if (!clienteId) return;
    obterClienteApi(clienteId)
      .then((c) => {
        setCli(c);
        setComp(c?.competencia_final ? String(c.competencia_final).slice(0, 7) : '');
      })
      .catch(() => setCli(null));
  };
  useEffect(carregar, [clienteId]);

  const gravar = async (reabrir) => {
    setMsg(null);
    if (!reabrir && !/^\d{4}-\d{2}$/.test(comp)) {
      return setMsg({ tipo: 'danger', txt: 'Informe a competência final no formato AAAA-MM.' });
    }
    if (!motivo.trim()) {
      return setMsg({ tipo: 'danger',
        txt: reabrir ? 'Descreva por que a carteira está voltando.'
                     : 'Descreva o motivo: distrato, transferência ou baixa.' });
    }
    setOcupado(true);
    try {
      const r = await atualizarConfigCliente(clienteId, {
        competencia_final: reabrir ? null : comp,
        competencia_final_motivo: motivo.trim(),
      });
      setMsg({ tipo: 'success',
               txt: reabrir ? 'Carteira reaberta: o cliente volta a ser responsabilidade da Átrio.'
                            : `Responsabilidade encerrada em ${comp.split('-').reverse().join('/')}.` });
      setMotivo('');
      setCli(r);
      onMudou?.(r);
    } catch (e) {
      setMsg({ tipo: 'danger', txt: e.message });
    } finally { setOcupado(false); }
  };

  if (!clienteId) return null;
  const encerrado = !!cli?.competencia_final;

  return (
    <div style={{
      border: `1px solid ${encerrado ? 'var(--al-critica-fg)' : 'var(--line, #d5dce8)'}`,
      borderRadius: 8, padding: 16, marginBottom: 16,
      background: encerrado ? 'var(--al-critica-bg)' : 'var(--surface, #fff)',
    }}>
      <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>
          {encerrado ? '📕 Carteira encerrada' : '📗 Cliente ativo na carteira'}
        </h3>
        {encerrado && (
          <span style={{ fontWeight: 700, color: 'var(--al-critica-fg)' }}>
            até {comoCompetencia(cli.competencia_final)}
          </span>
        )}
      </div>

      {encerrado && (
        <p className="small" style={{ margin: '0 0 12px' }}>
          {cli.competencia_final_motivo}
          {cli.competencia_final_por && <> · por {cli.competencia_final_por}</>}
          {cli.competencia_final_em && <> em {new Date(cli.competencia_final_em).toLocaleDateString('pt-BR')}</>}
        </p>
      )}

      {msg && <Alert tipo={msg.tipo}>{msg.txt}</Alert>}

      <div className="flex" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ fontSize: 12 }}>
          Última competência nossa<br />
          <input value={comp} onChange={(e) => setComp(e.target.value)}
                 placeholder="2026-06" style={{ width: 110 }} />
        </label>
        <label style={{ flex: 1, minWidth: 220, fontSize: 12 }}>
          Motivo<br />
          <input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                 placeholder={encerrado ? 'por que a carteira está voltando' : 'distrato, transferência ou baixa'}
                 style={{ width: '100%' }} />
        </label>
        <button className="primary" onClick={() => gravar(false)} disabled={ocupado}>
          {encerrado ? 'Alterar a competência' : 'Encerrar a carteira'}
        </button>
        {encerrado && (
          <button onClick={() => gravar(true)} disabled={ocupado}>Reabrir</button>
        )}
      </div>

      <p className="small muted" style={{ margin: '12px 0 0' }}>
        Marcada a competência final, escolher um mês posterior passa a exibir aviso nos
        relatórios e nos lançamentos. O aviso não impede escriturar: para travar de vez,
        feche os períodos. O registro comercial do distrato continua na carteira do GestHub.
      </p>
    </div>
  );
}
